import { Link } from 'react-router-dom';
import { FaFacebook, FaInstagram, FaTwitter, FaYoutube, FaWhatsapp, FaEnvelope, FaPhone, FaHeart } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { CONTACT, SOCIAL_LINKS, NAV_LINKS } from '../../utils/constants';

const socialLinks = [
  { name: 'Facebook', url: SOCIAL_LINKS.facebook, icon: <FaFacebook />, color: '#1877F2' },
  { name: 'Instagram', url: SOCIAL_LINKS.instagram, icon: <FaInstagram />, color: '#E4405F' },
  { name: 'YouTube', url: SOCIAL_LINKS.youtube, icon: <FaYoutube />, color: '#FF0000' },
  { name: 'WhatsApp', url: CONTACT.whatsappLink, icon: <FaWhatsapp />, color: '#25D366' },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      className="relative overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, #1A2E1C 0%, #0F1C10 60%, #1A1F2E 100%)',
        color: 'rgba(255,255,255,0.85)',
      }}
    >
      {/* Top rainbow stripe */}
      <div className="h-1 w-full" style={{
        background: 'linear-gradient(90deg, #FF3B3B, #FF7F00, #F9C513, #3CB044, #1565C0, #7B2FBE)'
      }} />

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {['📚', '⭐', '🌈', '🦕', '🎨'].map((e, i) => (
          <div
            key={i}
            className="absolute text-3xl md:text-5xl opacity-4 select-none"
            style={{ left: `${i * 22}%`, top: `${15 + (i % 2) * 50}%`, opacity: 0.05 }}
          >{e}</div>
        ))}
      </div>

      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-14 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-5 group">
              <img
                src="/logo.png"
                alt="The Tiny Times"
                className="h-14 md:h-16 w-auto object-contain opacity-95 group-hover:opacity-100 transition-opacity"
                style={{ maxWidth: '200px', filter: 'brightness(1.1)' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </Link>
            <p className="text-sm leading-relaxed mb-5" style={{ color: 'rgba(255,255,255,0.65)' }}>
              India's favourite weekly children's magazine. Fun stories, puzzles & learning for ages 4–15.
              Making education an adventure every week! 🚀
            </p>
            {/* Social Icons */}
            <div className="flex gap-3 flex-wrap">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.15, y: -3 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-lg transition-colors"
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    color: 'rgba(255,255,255,0.8)',
                  }}
                  aria-label={social.name}
                  onMouseEnter={(e) => { e.currentTarget.style.background = social.color; e.currentTarget.style.borderColor = social.color; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-display font-bold text-white text-lg mb-5 flex items-center gap-2">
              <span className="w-6 h-0.5 rounded-full inline-block" style={{ background: 'linear-gradient(90deg, #3CB044, #1565C0)' }} />
              Quick Links
            </h3>
            <ul className="space-y-2.5">
              {NAV_LINKS.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm flex items-center gap-2 transition-all hover:translate-x-1.5 group"
                    style={{ color: 'rgba(255,255,255,0.65)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#6DD05A'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
                  >
                    <span className="text-primary-green-light opacity-70 group-hover:opacity-100">{link.icon}</span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Our Editions */}
          <div>
            <h3 className="font-display font-bold text-white text-lg mb-5 flex items-center gap-2">
              <span className="w-6 h-0.5 rounded-full inline-block" style={{ background: 'linear-gradient(90deg, #7B2FBE, #1565C0)' }} />
              Our Editions
            </h3>
            <div className="space-y-4">
              <Link to="/editions" className="block group">
                <div
                  className="rounded-xl p-4 transition-all"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(60,176,68,0.12)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                >
                  <div className="font-display font-bold text-white text-sm flex items-center gap-2 mb-1">
                    <span>🌈</span> Micro Edition
                  </div>
                  <div className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>Ages 4–9 years</div>
                  <div className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>Fun stories, coloring, picture puzzles</div>
                </div>
              </Link>
              <Link to="/editions" className="block group">
                <div
                  className="rounded-xl p-4 transition-all"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(21,101,192,0.12)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                >
                  <div className="font-display font-bold text-white text-sm flex items-center gap-2 mb-1">
                    <span>🚀</span> Macro Edition
                  </div>
                  <div className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>Ages 10–15 years</div>
                  <div className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>News, logic puzzles, science experiments</div>
                </div>
              </Link>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-display font-bold text-white text-lg mb-5 flex items-center gap-2">
              <span className="w-6 h-0.5 rounded-full inline-block" style={{ background: 'linear-gradient(90deg, #F57C00, #D32F2F)' }} />
              Get In Touch
            </h3>
            <ul className="space-y-3">
              <li>
                <a href={`mailto:${CONTACT.email}`} className="flex items-start gap-3 text-sm group" style={{ color: 'rgba(255,255,255,0.65)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#FFA726'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}>
                  <FaEnvelope className="text-base mt-0.5 flex-shrink-0 text-accent-orange" />
                  <span className="break-all">{CONTACT.email}</span>
                </a>
              </li>
              <li>
                <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-3 text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#42A5F5'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}>
                  <FaPhone className="text-base flex-shrink-0" style={{ color: '#42A5F5' }} />
                  <span>{CONTACT.phone}</span>
                </a>
              </li>
              <li>
                <a href={CONTACT.whatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#25D366'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}>
                  <FaWhatsapp className="text-lg flex-shrink-0" style={{ color: '#25D366' }} />
                  <span>Chat on WhatsApp</span>
                </a>
              </li>
            </ul>

            {/* WhatsApp Subscribe CTA */}
            <motion.a
              href={CONTACT.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="mt-6 flex items-center justify-center gap-2 w-full py-3 rounded-xl font-display font-semibold text-sm text-white"
              style={{
                background: 'linear-gradient(135deg, #25D366, #128C7E)',
                boxShadow: '0 4px 16px rgba(37,211,102,0.25)',
              }}
            >
              <FaWhatsapp className="text-lg" />
              Subscribe via WhatsApp
            </motion.a>
          </div>

        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="container mx-auto px-4 py-5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
            <div className="flex items-center gap-1.5">
              © {currentYear} The Tiny Times. All rights reserved. Made with
              <FaHeart className="text-red-400 text-xs" />
              for children.
            </div>
            <div className="flex gap-5">
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/subscribe" className="hover:text-white transition-colors">Subscribe</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;